import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { documentsApi } from '../api/documents.api'
import type { PartnerRef } from '../types/document.types'

interface Props {
  value: { id: string | null; name: string }
  role?: 'customer' | 'supplier'
  disabled?: boolean
  onSelect: (partner: PartnerRef) => void
}

/** AP-32 13.2 — Partner-Suchfeld der Belegmaske (Nummer oder Name). */
export function PartnerPicker({ value, role, disabled, onSelect }: Props) {
  const { t } = useTranslation()
  const [text, setText] = useState(value.name)
  const [treffer, setTreffer] = useState<PartnerRef[]>([])
  const [offen, setOffen] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => setText(value.name), [value.name])

  const suche = (q: string) => {
    setText(q)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(async () => {
      try {
        const { data } = await documentsApi.partners(q, role)
        setTreffer(data.data)
        setOffen(true)
      } catch {
        setTreffer([])
      }
    }, 250)
  }

  return (
    <div className="relative">
      <input
        disabled={disabled}
        value={text}
        placeholder={t('documents.partner.searchPlaceholder')}
        onChange={(e) => suche(e.target.value)}
        onFocus={() => suche(text === value.name ? '' : text)}
        onBlur={() => setTimeout(() => setOffen(false), 150)}
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
      />
      {offen && treffer.length > 0 && (
        <ul
          className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-md border border-border shadow-lg"
          style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}
        >
          {treffer.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onSelect(p)
                  setText(p.name)
                  setOffen(false)
                }}
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
              >
                <span className="font-medium">{p.name}</span>
                <span className="text-xs text-muted-foreground">
                  {role === 'supplier' ? (p.supplierNumber ?? p.clientNumber) : p.clientNumber}
                  {p.city ? ` · ${p.city}` : ''}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
